// src/hooks/useIdleLogout.ts
// Автовыход через час бездействия
import { useEffect, useRef, useCallback, useState } from 'react';

const IDLE_TIMEOUT = 60 * 60 * 1000;       // 60 минут — выход
const WARNING_TIMEOUT = 55 * 60 * 1000;    // 55 минут — предупреждение
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];
const STORAGE_KEY = 'hrlama_last_activity';

interface Options {
  onLogout: () => void;
  enabled?: boolean;
  warningEnabled?: boolean;
}

export function useIdleLogout({ onLogout, enabled = true, warningEnabled = true }: Options) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(300);

  // Все refs — стабильные, не вызывают re-render
  const onLogoutRef = useRef(onLogout);
  const enabledRef = useRef(enabled);
  const warningEnabledRef = useRef(warningEnabled);

  const lastActivityRef = useRef<number>(Date.now());
  const logoutTimerRef = useRef<any>(null);
  const warningTimerRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);
  const warnedRef = useRef(false);
  const loggedOutRef = useRef(false);  // ★ защита от повторного вызова

  // Синхронизация props с refs
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { warningEnabledRef.current = warningEnabled; }, [warningEnabled]);

  // ─── Единая функция выхода (стабильная) ───
  const doLogout = useCallback(() => {
    console.log('[idle] doLogout called, loggedOutRef:', loggedOutRef.current);
    if (loggedOutRef.current) {
      console.log('[idle] ⚠️ Already logged out, skipping');
      return;
    }
    loggedOutRef.current = true;
    localStorage.removeItem(STORAGE_KEY);
    setShowWarning(false);
    try {
      console.log('[idle] → calling onLogout');
      onLogoutRef.current();
    } catch (e) {
      console.error('[idle] logout err:', e);
    }
  }, []);

  // ─── Сброс таймеров (стабильная) ───
  const resetTimers = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    warnedRef.current = false;
    loggedOutRef.current = false;  // ★ сбрасываем при новой сессии
    setShowWarning(false);

    // Предупреждение за 5 минут
    if (warningEnabledRef.current) {
      warningTimerRef.current = setTimeout(() => {
        warnedRef.current = true;
        setShowWarning(true);
        setSecondsLeft(300);
        countdownRef.current = setInterval(() => {
          setSecondsLeft(prev => {
            if (prev <= 1) {
              if (countdownRef.current) clearInterval(countdownRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, WARNING_TIMEOUT);
    }

    // Полный выход
    logoutTimerRef.current = setTimeout(() => {
      doLogout();
    }, IDLE_TIMEOUT);
  }, [doLogout]);

  // ─── ОСНОВНОЙ EFFECT ───
  // ВАЖНО: deps только [enabled] — не добавляем resetTimers/doLogout
  // (они стабильные, но React может ругаться на exhaustive-deps)
  useEffect(() => {
    if (!enabled) return;

    const now = Date.now();

    // Проверка при монтировании
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const last = parseInt(stored, 10);
        if (!isNaN(last)) {
          if (Date.now() - last >= IDLE_TIMEOUT) {
            doLogout();
            return;
          }
          if (Date.now() - last < IDLE_TIMEOUT) {
            lastActivityRef.current = last;
          }
        }
      }
    } catch (e) {}

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      try { localStorage.setItem(STORAGE_KEY, String(lastActivityRef.current)); } catch (e) {}
      if (!warnedRef.current) {
        resetTimers();
      }
    };

    // Throttle mousemove — сбрасываем не чаще раза в секунду
    let lastReset = 0;
    const throttledActivity = () => {
      const t = Date.now();
      if (t - lastReset < 1000) return;
      lastReset = t;
      handleActivity();
    };

    ACTIVITY_EVENTS.forEach(evt => {
      const fn = evt === 'mousemove' ? throttledActivity : handleActivity;
      window.addEventListener(evt, fn, { passive: true });
    });

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const last = parseInt(e.newValue, 10);
        if (!isNaN(last) && Date.now() - last < IDLE_TIMEOUT) {
          lastActivityRef.current = last;
          if (!warnedRef.current) resetTimers();
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    const handleVisible = () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const last = parseInt(stored, 10);
          if (!isNaN(last) && Date.now() - last >= IDLE_TIMEOUT) {
            doLogout();
            return;
          }
          if (!isNaN(last) && Date.now() - last < IDLE_TIMEOUT && !warnedRef.current) {
            lastActivityRef.current = last;
            resetTimers();
          }
        }
      } catch (e) {}
    };
    document.addEventListener('visibilitychange', handleVisible);

    // Старт таймеров
    resetTimers();
    try { localStorage.setItem(STORAGE_KEY, String(now)); } catch (e) {}

    return () => {
      ACTIVITY_EVENTS.forEach(evt => {
        const fn = evt === 'mousemove' ? throttledActivity : handleActivity;
        window.removeEventListener(evt, fn);
      });
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisible);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // ← ТОЛЬКО enabled! resetTimers/doLogout стабильные

  // ★ Защита: если таймер показал 0 — НЕМЕДЛЕННЫЙ выход
  useEffect(() => {
    if (showWarning && secondsLeft === 0 && !loggedOutRef.current) {
      console.log('[idle] ⏰ Timer = 0 — forcing logout NOW');
      doLogout();
    }
  }, [showWarning, secondsLeft, doLogout]);

  const extend = useCallback(() => {
    lastActivityRef.current = Date.now();
    loggedOutRef.current = false;  // ★ пользователь вернулся — сбрасываем
    try { localStorage.setItem(STORAGE_KEY, String(lastActivityRef.current)); } catch (e) {}
    resetTimers();
  }, [resetTimers]);

  const logoutNow = useCallback(() => {
    doLogout();
  }, [doLogout]);

  return { showWarning, secondsLeft, extend, logoutNow };
}

export default useIdleLogout;
